/**
 * useOjbTeamRoster — one-shot fetch of the full OJB roster with a
 * generous staleTime, so multiple consumers on the same page (the
 * attendee typeahead in OjbMemberPicker, the OJB member-pill avatars
 * in AttendeesPanel, etc.) dedupe onto a single request.
 *
 * Returns:
 *   - `members` — array of team members (same shape the /ojb-team
 *     route returns for non-admin callers, with headshotUrl)
 *   - `byName` — Map<normalizedName, headshotUrl | null> for O(1)
 *     avatar lookup from a pill's display name
 *
 * Name normalization: lowercase + trim + collapse internal whitespace.
 * Also registers a fallback key with credentials stripped ("sara
 * chen, fasla" → "sara chen") so free-typed-with-creds names still
 * resolve to a photo.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient.ts';

export interface RosterMember {
  id: string;
  fullName: string;
  /** OJB email — exposed to non-admins because the New Meeting
   *  attendee picker needs it to build AttendeeEmail records. */
  email?: string | null;
  office: string | null;
  title: string | null;
  headshotUrl: string | null;
}

function normalize(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function stripCreds(name: string): string {
  // "Sara Chen, FASLA" → "Sara Chen"; leaves "Sara Chen" unchanged.
  const comma = name.indexOf(',');
  return (comma >= 0 ? name.slice(0, comma) : name).trim();
}

export function useOjbTeamRoster() {
  const query = useQuery({
    // Stable key (no q param) so every caller dedupes onto this
    // single request regardless of where on the page they live.
    queryKey: ['ojb-team-roster-full'],
    queryFn: () =>
      api.get<{ members: RosterMember[] }>('/ojb-team', { limit: 500 }),
    staleTime: 10 * 60 * 1000, // 10 min — roster changes infrequently
    gcTime: 30 * 60 * 1000,
  });

  const byName = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of query.data?.members ?? []) {
      map.set(normalize(m.fullName), m.headshotUrl ?? null);
      const bare = normalize(stripCreds(m.fullName));
      if (!map.has(bare)) {
        map.set(bare, m.headshotUrl ?? null);
      }
    }
    return map;
  }, [query.data]);

  const lookupPhoto = (displayName: string): string | null => {
    if (!displayName) return null;
    const direct = byName.get(normalize(displayName));
    if (direct !== undefined) return direct;
    // Fallback: user typed "Sara Chen, AIA" but roster has bare name
    const bare = byName.get(normalize(stripCreds(displayName)));
    return bare ?? null;
  };

  return {
    members: query.data?.members ?? [],
    byName,
    lookupPhoto,
    isLoading: query.isLoading,
  };
}
